import { differenceInDays } from 'date-fns';
import { safeMoneyFromCents } from '@libs/services/money';
import { Order } from 'src/trpc';

// Extract ContainerPricing type from Order's quotedPricingDetails
type QuotedPricingDetails = NonNullable<Order['quotedPricingDetails']>;
type Quote = NonNullable<QuotedPricingDetails['quote']>;
type ContainerPricing = Quote['pricing']['containers'][number];

interface BundlePricingCardProps {
  containerPricing: ContainerPricing;
  dropOffDate?: string;
  pickupDate?: string;
}

export const BundlePricingCard = ({
  containerPricing,
  dropOffDate,
  pickupDate,
}: BundlePricingCardProps) => {
  const { name, quantity = 1, basePriceInCents, periodTier } = containerPricing;

  // Calculate actual days and overages
  const calculateDaysInfo = () => {
    if (!pickupDate || !dropOffDate || !periodTier) {
      return null;
    }

    const actualDays = differenceInDays(new Date(pickupDate), new Date(dropOffDate));
    const includedDays = periodTier.selectedPeriodDays;
    const overageDays = Math.max(0, actualDays - includedDays);
    const overageRate = periodTier.additionalDayRateInCents;

    return {
      actualDays,
      includedDays,
      overageDays,
      overageRate,
    };
  };

  const daysInfo = calculateDaysInfo();

  return (
    <div className="card p-4 mt-2 bg-primary-50 border-primary-200 w-full">
      <div className="flex flex-col gap-2 w-full self-start">
        <h3 className="font-semibold text-lg text-primary-900">Flat Rate Package</h3>

        {/* Bundle Details */}
        <div className="grid grid-cols-2 gap-4 w-full">
          <div className="flex flex-col w-full">
            <p className="label text-sm">Included in Package</p>
            <p className="text-text-primary text-sm font-medium">
              {periodTier?.selectedPeriodDays} service days
            </p>
            <p className="text-text-primary text-sm font-medium">
              {periodTier?.includedTonnage} tons disposal
            </p>
          </div>

          {/* Show overages if any */}
          <div className="flex flex-col w-full">
            <p className="label text-sm">Usage & Overages</p>
            {daysInfo && (
              <>
                <p className="text-text-primary text-sm">
                  {daysInfo.actualDays} days total
                  {daysInfo.overageDays > 0 && (
                    <span className="text-warning ml-1">(+{daysInfo.overageDays} extra)</span>
                  )}
                </p>
              </>
            )}
            {/* Weight will be calculated at final pickup */}
            <p className="text-text-light text-xs mt-1">
              Weight overages calculated after pickup at{' '}
              {safeMoneyFromCents(periodTier?.additionalTonRateInCents || 0).toFormat()}
              /ton
            </p>
          </div>
        </div>

        {/* Dumpster info - placed above separator */}
        <div className="mt-2">
          <p className="text-text-primary text-sm">{name} Dumpster</p>
          <p className="text-text-light text-xs">
            {quantity} dumpster{quantity > 1 ? 's' : ''}
          </p>
        </div>

        {/* Flat Rate Pricing Breakdown */}
        <div className="mt-3 pt-3 border-t border-primary-100">
          <div className="flex flex-row justify-between text-sm">
            <p>Base Price</p>
            <p>{safeMoneyFromCents(basePriceInCents).toFormat()}</p>
          </div>
          {/* Show day overage charge if applicable */}
          {daysInfo &&
            daysInfo.overageDays > 0 &&
            daysInfo.overageRate &&
            daysInfo.overageRate > 0 && (
              <div className="flex flex-row justify-between text-sm mt-1">
                <p className="text-text-light text-sm">
                  {daysInfo.overageDays} extra day(s) ×{' '}
                  {safeMoneyFromCents(daysInfo.overageRate).toFormat()}
                </p>
                <p className="text-text-light text-sm">
                  {safeMoneyFromCents(daysInfo.overageDays * daysInfo.overageRate).toFormat()}
                </p>
              </div>
            )}
        </div>
      </div>
    </div>
  );
};
